/**
 * DashboardTabs - Tabs de navegação entre dashboards do CRM
 * 
 * Exibe dashboards disponíveis para o usuário em formato de abas.
 * Semelhante ao DealPipelineTabs, mas sem opções de criação/edição.
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { Dashboard } from '@/hooks/useDashboards';

interface DashboardTabsProps {
  // Lista de dashboards disponíveis
  dashboards: Dashboard[];
  // ID do dashboard selecionado
  selectedDashboardId: string | null;
  // Callback ao selecionar dashboard
  onSelectDashboard: (id: string) => void;
  // Estado de carregamento
  isLoading?: boolean;
  /** Força a exibição das tabs mesmo havendo apenas 1 dashboard. */
  alwaysShow?: boolean;
}

/**
 * Componente de tabs para navegação entre dashboards CRM
 */
export function DashboardTabs({
  dashboards,
  selectedDashboardId,
  onSelectDashboard,
  isLoading = false,
  alwaysShow = false,
}: DashboardTabsProps) {
  // Exibe skeleton durante carregamento
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 pb-1">
        <Skeleton className="h-9 w-32 rounded-md" />
        <Skeleton className="h-9 w-28 rounded-md" />
        <Skeleton className="h-9 w-36 rounded-md" />
      </div>
    );
  }

  // Se não há dashboards, não exibe nada
  if (dashboards.length === 0) {
    return null;
  }

  // Se há apenas 1 dashboard e não foi pedido para sempre mostrar, oculta
  if (dashboards.length === 1 && !alwaysShow) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 w-full overflow-x-auto scrollbar-hide pb-1 -mb-1">
      <Tabs 
        value={selectedDashboardId || ''} 
        onValueChange={onSelectDashboard}
        className="w-full"
      >
        <TabsList className="h-9 bg-muted/50">
          {dashboards.map((dashboard) => (
            <TabsTrigger
              key={dashboard.id}
              value={dashboard.id}
              className={cn(
                'px-4 text-sm whitespace-nowrap',
                'data-[state=active]:bg-background',
                'transition-all duration-200'
              )}
              style={{ 
                borderBottomColor: selectedDashboardId === dashboard.id 
                  ? dashboard.color 
                  : 'transparent',
                borderBottomWidth: 2,
              }}
            >
              {/* Indicador de cor do dashboard */}
              <div 
                className="w-2 h-2 rounded-full mr-2 flex-shrink-0"
                style={{ backgroundColor: dashboard.color }}
              />
              {dashboard.name}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  );
}
